import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useFeedbackById } from '@/hooks/usePerformance';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { MarkdownText } from '@/components/performance/MarkdownText';

export function PerformanceFeedbackResult() {
  const { feedbackId } = useParams<{ feedbackId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backTarget = (location.state as { from?: string } | null)?.from ?? '/performance/history';
  const { t } = useTranslation();
  const { data: feedback, isLoading } = useFeedbackById(feedbackId!);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  const gc = feedback?.generalComments;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" aria-label={t('common.a11y.back')} onClick={() => navigate(backTarget)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{t('performance.feedback_result.title')}</h1>
          <p className="text-muted-foreground text-sm">
            {feedback?.clapperName} {feedback?.clapperLastName}
          </p>
        </div>
      </div>

      <Card className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20">
        <CardContent className="flex items-center gap-3 pt-4">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          <p className="text-sm text-emerald-700 dark:text-emerald-400">
            {t('performance.feedback_result.completed_message')}
          </p>
        </CardContent>
      </Card>

      {feedback?.summary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('performance.feedback_result.summary_title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <MarkdownText className="text-sm">{feedback.summary}</MarkdownText>
          </CardContent>
        </Card>
      )}

      {gc && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: 'effective', label: t('performance.analysis.strengths') },
            { key: 'affective', label: t('performance.analysis.improvements') },
            { key: 'perspective', label: t('performance.analysis.action_plan') },
          ].map(({ key, label }) => (
            <Card key={key}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{label}</CardTitle>
              </CardHeader>
              <CardContent>
                <MarkdownText className="text-sm text-muted-foreground">
                  {(gc as any)[key] || '—'}
                </MarkdownText>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex gap-3">
        <Button variant="outline" onClick={() => navigate('/performance')}>
          {t('performance.feedback_result.new_evaluation')}
        </Button>
        <Button variant="outline" onClick={() => navigate('/performance/history')}>
          {t('performance.feedback_result.view_history')}
        </Button>
      </div>
    </div>
  );
}
